/*
 * The article's Attachments, under its images in the Text card (S47).
 *
 * An attachment is a LIBRARY file - kept once in the deployment's storage
 * account - and this panel is the article's LIST of them: what a reader sees
 * under the text, at most ten (ATTACHMENT_MAX_PER_ARTICLE). So the verbs are
 * the list's. "Upload new" puts a file in the library and on this list in one
 * request; "Add from library" lists a file the library already has, which is
 * how one manual comes to be shared by several articles; "Remove" takes it off
 * this list and nothing more - the library keeps the file, every other article
 * keeps it, and a link to it in this text keeps working. Replacing or deleting
 * the file itself is the library page's business, because it changes every
 * article that uses it.
 *
 * "Insert link" puts an [[attachment:<id>]] token at the caret: a link in the
 * text that downloads the file, listed or not. A changed list is saved as it
 * happens - it is not part of the revision - like an uploaded image.
 *
 * Its words come from the chosen language's dictionary, t.admin.editor (S48).
 */

import { useMemo, useRef, useState } from 'react';
import {
  ATTACHMENT_MAX_PER_ARTICLE,
  buildAttachmentLinkToken,
  collectAttachmentLinkIds,
  type AdminAttachment,
} from '@accotool-kb/shared';
import { useAdminApi } from '../../auth/AuthProvider.js';
import { useAction, useAsync, useDebounced } from '../../hooks/useAsync.js';
import { useAttachmentDownload } from '../../hooks/useAttachmentDownload.js';
import { useAttachmentsEnabled } from '../../hooks/useAttachmentsEnabled.js';
import { ErrorBanner, Loading, Modal } from '../../components/ui.js';
import { en, type Messages } from '../../i18n/en.js';
import { useMessages } from '../../i18n/language.js';
import { richText } from '../../i18n/richText.js';
import {
  AttachmentFilePicker,
  AttachmentUploadList,
  useAttachmentUploads,
  type AttachmentFilePickerHandle,
} from './AttachmentUploads.js';

/** The library's order, as the server lists: by name, case aside. */
export function byFileName(a: AdminAttachment, b: AdminAttachment): number {
  return a.fileName.localeCompare(b.fileName, undefined, { sensitivity: 'base' }) || a.attachmentId.localeCompare(b.attachmentId);
}

export function describeAttachmentKind(
  attachment: { fileName: string; contentType: string },
  messages: Messages = en,
): string {
  return messages.article.fileType(attachment.fileName, attachment.contentType);
}

export function ArticleAttachmentsPanel({
  articleId,
  content,
  attachments,
  onAttachmentsChanged,
  onInsertLink,
}: {
  articleId: string;
  /** The text as typed, to say which files it links. */
  content: string;
  attachments: AdminAttachment[];
  onAttachmentsChanged: (next: AdminAttachment[]) => void;
  /** Puts the token at the editor's caret. */
  onInsertLink: (token: string) => void;
}): JSX.Element {
  const t = useMessages();
  const words = t.admin.editor.attachments;
  const api = useAdminApi();
  const enabled = useAttachmentsEnabled();
  const action = useAction();
  const downloads = useAttachmentDownload();
  const picker = useRef<AttachmentFilePickerHandle>(null);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  // Read through a ref by the upload callback, which finishes long after the
  // click that started it: the list it adds to must be the list as it is then.
  const current = useRef(attachments);
  current.current = attachments;

  const uploads = useAttachmentUploads((response) => {
    onAttachmentsChanged([...current.current, response.attachment].sort(byFileName));
    setNotice(words.uploaded(response.attachment.fileName));
  });

  const linked = useMemo(() => new Set(collectAttachmentLinkIds(content)), [content]);
  const full = attachments.length >= ATTACHMENT_MAX_PER_ARTICLE;

  const remove = async (attachment: AdminAttachment): Promise<void> => {
    const done = await action.run(async () => {
      await api.unlistAttachmentFromArticle(articleId, attachment.attachmentId);
      return true;
    });
    if (!done) return;
    onAttachmentsChanged(current.current.filter((entry) => entry.attachmentId !== attachment.attachmentId));
    setNotice(words.unlisted(attachment.fileName, linked.has(attachment.attachmentId)));
  };

  return (
    <div className="image-panel attachment-panel">
      <div className="card-header">
        <h3>{words.heading}</h3>
        <span className="attachment-panel-actions">
          <span className="faint" title={t.admin.rules.attachmentCountLimit}>
            {attachments.length === 0 ? words.noneListed : words.count(attachments.length, ATTACHMENT_MAX_PER_ARTICLE)}
          </span>
          <button
            type="button"
            className="button button-small button-quiet"
            onClick={() => setLibraryOpen(true)}
            disabled={full || action.running}
            title={full ? words.full : words.addFromLibraryHint}
          >
            {words.addFromLibrary}
          </button>
          <button
            type="button"
            className="button button-small"
            onClick={() => picker.current?.open()}
            disabled={!enabled || full}
            title={!enabled ? words.noStore : full ? words.full : words.uploadNewHint}
          >
            {words.uploadNew}
          </button>
          <AttachmentFilePicker
            ref={picker}
            multiple
            label={words.chooseFiles}
            onFiles={(files) =>
              void uploads.start(files, (file, fileName, onProgress) =>
                api.uploadArticleAttachment(articleId, file, { fileName, onProgress }),
              )
            }
          />
        </span>
      </div>

      {!enabled && <AttachmentStoreOffNotice />}
      {action.error && <ErrorBanner error={action.error} onDismiss={action.clearError} />}
      {downloads.error && <ErrorBanner error={downloads.error} onDismiss={downloads.clearError} />}
      {notice && (
        <div className="notice notice-success" role="status">
          <div className="notice-body">{notice}</div>
          <div className="notice-actions">
            <button type="button" className="button button-quiet" onClick={() => setNotice(null)}>
              {t.common.dismiss}
            </button>
          </div>
        </div>
      )}

      <AttachmentUploadList uploads={uploads.uploads} dismiss={uploads.dismiss} />

      {attachments.length === 0 ? (
        <p className="faint">{words.empty}</p>
      ) : (
        <ul className="attachment-list">
          {attachments.map((attachment) => (
            <li key={attachment.attachmentId} className="attachment-list-row">
              <div className="attachment-list-detail">
                <span className="attachment-name">{attachment.fileName}</span>
                <span className="faint">
                  {describeAttachmentKind(attachment, t)} · {t.article.byteSize(attachment.byteLength)}
                  {attachment.listedBy > 1 && ` · ${words.alsoListedBy(attachment.listedBy - 1)}`}
                </span>
              </div>
              <div className="attachment-list-usage">
                {linked.has(attachment.attachmentId) ? (
                  <span className="faint">{words.linkedInText}</span>
                ) : null}
              </div>
              <div className="button-row">
                <button
                  type="button"
                  className="button button-small button-quiet"
                  disabled={!enabled || downloads.running}
                  onClick={() => void downloads.download(() => api.attachmentDownloadLink(attachment.attachmentId))}
                >
                  {words.download}
                </button>
                <button
                  type="button"
                  className="button button-small"
                  onClick={() => onInsertLink(buildAttachmentLinkToken(attachment.attachmentId))}
                  title={words.insertLinkHint}
                >
                  {words.insertLink}
                </button>
                <button
                  type="button"
                  className="button button-small button-quiet"
                  disabled={action.running}
                  onClick={() => void remove(attachment)}
                  title={words.removeHint}
                >
                  {words.remove}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <AttachmentLibraryPicker
        open={libraryOpen}
        articleId={articleId}
        listed={attachments}
        onClose={() => setLibraryOpen(false)}
        onListed={(attachment) => {
          onAttachmentsChanged(
            [...current.current.filter((entry) => entry.attachmentId !== attachment.attachmentId), attachment].sort(
              byFileName,
            ),
          );
          setNotice(words.listed(attachment.fileName));
          setLibraryOpen(false);
        }}
      />
    </div>
  );
}

/** Said wherever an upload or a download cannot work on this deployment. */
export function AttachmentStoreOffNotice(): JSX.Element {
  const words = useMessages().admin.editor.attachments;
  return (
    <div className="notice notice-info">
      <div className="notice-body">
        <strong>{words.noStore}</strong>
        <p style={{ marginBottom: 0 }}>
          {richText(words.noStoreBody, { setting: <code>ATTACHMENTS_BLOB_ENDPOINT</code> })}
        </p>
      </div>
    </div>
  );
}

/**
 * Lists a library file on the article: the library, searched by name, without
 * what the article already lists. The server refuses a second file of a name
 * the list already shows, and the refusal is shown here, where it was asked.
 */
function AttachmentLibraryPicker({
  open,
  articleId,
  listed,
  onClose,
  onListed,
}: {
  open: boolean;
  articleId: string;
  listed: AdminAttachment[];
  onClose: () => void;
  onListed: (attachment: AdminAttachment) => void;
}): JSX.Element | null {
  const t = useMessages();
  const words = t.admin.editor.attachmentLibrary;
  const api = useAdminApi();
  const action = useAction();
  const [searchDraft, setSearchDraft] = useState('');
  const search = useDebounced(searchDraft.trim().toLowerCase(), 150);
  const library = useAsync(() => (open ? api.listAttachments() : Promise.resolve(null)), [open]);

  if (!open) return null;

  const listedIds = new Set(listed.map((entry) => entry.attachmentId));
  const items = (library.data ?? []).filter(
    (attachment) => !listedIds.has(attachment.attachmentId) && attachment.fileName.toLowerCase().includes(search),
  );

  const add = async (attachment: AdminAttachment): Promise<void> => {
    const result = await action.run(() => api.listAttachmentOnArticle(articleId, attachment.attachmentId));
    if (result) onListed(result.attachment);
  };

  return (
    <Modal
      open
      wide
      title={words.title}
      onClose={onClose}
      busy={action.running}
      footer={
        <>
          <span className="faint" style={{ marginRight: 'auto' }}>
            {words.sharedHint}
          </span>
          <button type="button" className="button" onClick={onClose} disabled={action.running}>
            {t.common.close}
          </button>
        </>
      }
    >
      <div className="field">
        <label htmlFor="attachment-library-search">{words.findLabel}</label>
        <input
          id="attachment-library-search"
          type="search"
          data-autofocus
          placeholder={words.findPlaceholder}
          value={searchDraft}
          onChange={(event) => setSearchDraft(event.target.value)}
        />
      </div>

      {action.error && <ErrorBanner error={action.error} onDismiss={action.clearError} />}
      {library.error && <ErrorBanner error={library.error} onRetry={library.reload} />}
      {library.loading && !library.data && <Loading label={words.loading} />}
      {library.data && items.length === 0 && (
        <p className="muted">{library.data.length === listedIds.size ? words.allListed : words.noMatch}</p>
      )}

      {items.length > 0 && (
        <ul className="attachment-list attachment-picker-list">
          {items.map((attachment) => (
            <li key={attachment.attachmentId} className="attachment-list-row">
              <div className="attachment-list-detail">
                <span className="attachment-name">{attachment.fileName}</span>
                <span className="faint">
                  {describeAttachmentKind(attachment, t)} · {t.article.byteSize(attachment.byteLength)} ·{' '}
                  {words.listedBy(attachment.listedBy)}
                </span>
              </div>
              <div className="button-row">
                <button
                  type="button"
                  className="button button-small button-primary"
                  disabled={action.running}
                  onClick={() => void add(attachment)}
                  aria-label={words.addFile(attachment.fileName)}
                >
                  {words.add}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}
